/**
 * «Ver como»: un operador recorre la app de un candidato, con los datos de esa
 * persona y sin poder cambiar nada, para saber qué está viendo y qué le falta.
 *
 * ── CÓMO ENTRA ─────────────────────────────────────────────────────────────
 *
 * Admin → Usuarios → «Ver como» abre una pestaña nueva en `/panel?ver_como=<id>`.
 * `captureViewAsFromUrl` corre en `main.tsx` ANTES de montar React (igual que
 * el `?ref` de los referidos): si la pestaña no tiene sesión, el rodeo por el
 * OIDC se come la query, pero `sessionStorage` sobrevive el viaje dentro de la
 * misma pestaña. Y es `sessionStorage` a propósito: la vista vive en ESA
 * pestaña; la del admin sigue siendo el operador.
 *
 * La URL lleva sólo el `_id`. El correo y el nombre para la franja de arriba
 * los deja el admin en `localStorage` justo antes de abrir la pestaña: no van
 * en la barra de direcciones ni en el historial.
 *
 * ── SOLO LECTURA ───────────────────────────────────────────────────────────
 *
 * Lo garantiza el servidor (`viewAsMiddleware`, 403 `view_as_read_only` a todo
 * lo que no sea GET). `apiFetch` además corta la escritura antes de salir, con
 * la misma respuesta, para que ningún botón llegue a intentarlo.
 *
 * Todo acceso al storage va en try/catch: en modo privado de Safari tira.
 */

/**
 * El recuerdo del alta de `useOnboardingGate` (su `REMEMBER_KEY`). Copiado y no
 * importado: importarlo cerraría un ciclo api → viewAs → gate → api.
 */
const ONBOARDING_REMEMBER_KEY = 'tbit.jobapply.onboarded';

export const VIEW_AS_KEY = 'job_apply_view_as';
const LABELS_KEY = 'job_apply_view_as_labels';
export const VIEW_AS_PARAM = 'ver_como';
export const VIEW_AS_HEADER = 'X-View-As';

const OBJECT_ID = /^[a-f0-9]{24}$/i;

export interface ViewAsLabel {
  email: string;
  name: string | null;
}

export interface ViewAs extends Partial<ViewAsLabel> {
  id: string;
}

/** La vista de esta pestaña, o null si el operador es él mismo. */
export function currentViewAs(): ViewAs | null {
  try {
    const raw = window.sessionStorage.getItem(VIEW_AS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ViewAs;
    return parsed && typeof parsed.id === 'string' && OBJECT_ID.test(parsed.id) ? parsed : null;
  } catch {
    return null;
  }
}

/** El admin deja a quién va a mirar, para que la pestaña nueva lo nombre. */
export function rememberViewAsLabel(id: string, label: ViewAsLabel): void {
  try {
    // Una sola entrada: la última que se abrió. No es una libreta de contactos.
    window.localStorage.setItem(LABELS_KEY, JSON.stringify({ [id]: label }));
  } catch {
    /* Sin storage la franja dice «otra persona»; la vista funciona igual. */
  }
}

function readLabel(id: string): ViewAsLabel | null {
  try {
    const raw = window.localStorage.getItem(LABELS_KEY);
    if (!raw) return null;
    const labels = JSON.parse(raw) as Record<string, ViewAsLabel>;
    return labels?.[id] ?? null;
  } catch {
    return null;
  }
}

/** `?ver_como=<id>` → la vista de esta pestaña. Se quita de la barra al leerla. */
export function captureViewAsFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get(VIEW_AS_PARAM);
    if (!id) return;
    url.searchParams.delete(VIEW_AS_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    if (!OBJECT_ID.test(id)) return;
    const label = readLabel(id);
    const entry: ViewAs = { id, ...(label ?? {}) };
    window.sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(entry));
    // El recuerdo del alta (useOnboardingGate) es de quien abrió la pestaña,
    // no de la persona que se va a mirar: que lo pregunte de nuevo.
    window.sessionStorage.removeItem(ONBOARDING_REMEMBER_KEY);
  } catch {
    /* Sin storage no hay vista: la pestaña abre como el operador. */
  }
}

/** Salir de la vista: la pestaña vuelve a ser del operador, en la lista de usuarios. */
export function exitViewAs(): void {
  try {
    window.sessionStorage.removeItem(VIEW_AS_KEY);
    window.sessionStorage.removeItem(ONBOARDING_REMEMBER_KEY);
  } catch {
    /* nada que limpiar */
  }
  window.location.assign('/admin/users');
}

/** Sólo las rutas del candidato van «como» otro; el admin es siempre el operador. */
export function appliesViewAs(path: string): boolean {
  return path.startsWith('/job-apply/') && !path.startsWith('/job-apply/api/admin');
}

/** La respuesta que da el servidor a una escritura durante la vista, sin salir a la red. */
export function readOnlyResponse(): Response {
  return new Response(
    JSON.stringify({ error: 'Vista de solo lectura', code: 'view_as_read_only' }),
    { status: 403, headers: { 'Content-Type': 'application/json' } },
  );
}
